import api from './api';

export interface PandoraDocument {
  id: string;
  title: string;
  filename?: string;
  category: string;
  processing_status: string;
  paragraph_count?: number;
  metadata?: Record<string, any>;
  ai_classification?: Record<string, any>;
  legal_domain?: string;
  uploaded_by?: string;
  validated_by?: string;
  validated_at?: string;
  promoted_to_id?: string;
  promoted_to_table?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PandoraDocumentDetail extends PandoraDocument {
  raw_content: string;
  paragraphs: PandoraParagraph[];
}

export interface PandoraParagraph {
  id: string;
  document_id: string;
  text: string;
  paragraph_index: number;
  taxonomy_tag?: string;
  tag_domain?: string;
  tag_class?: string;
  tag_element?: string;
  confidence?: number;
  status: string;
  curator_notes?: string;
  validated_by?: string;
  validated_at?: string;
  created_at?: string;
}

export interface PandoraTaxonomy {
  id: string;
  domain: string;
  class_name: string;
  element_name: string;
  full_tag: string;
  description?: string;
  is_active: boolean;
  created_at?: string;
}

export interface DocumentListResponse {
  items: PandoraDocument[];
  total: number;
  page: number;
  page_size: number;
}

export interface PipelineStats {
  total_documents: number;
  by_status: Record<string, number>;
  by_category: Record<string, number>;
  by_domain: Record<string, number>;
  total_paragraphs: number;
  validated_paragraphs: number;
  taxonomy_elements: number;
}

export interface ProcessingResponse {
  document_id: string;
  status: string;
  message: string;
}

const BASE = '/admin/pandora';

const pandoraAdminService = {
  // Upload
  async uploadText(data: { title: string; content: string; category: string; auto_process?: boolean }) {
    const res = await api.post(`${BASE}/upload-text`, data);
    return res.data as PandoraDocument;
  },

  async uploadFile(file: File, category: string, title?: string, autoProcess = false) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (title) formData.append('title', title);
    formData.append('auto_process', String(autoProcess));
    const res = await api.post(`${BASE}/upload`, formData);
    return res.data as PandoraDocument;
  },

  // Documents
  async listDocuments(params: {
    status?: string;
    category?: string;
    legal_domain?: string;
    search?: string;
    page?: number;
    page_size?: number;
  } = {}) {
    const res = await api.get(`${BASE}/documents`, { params });
    return res.data as DocumentListResponse;
  },

  async getDocument(id: string) {
    const res = await api.get(`${BASE}/documents/${id}`);
    return res.data as PandoraDocumentDetail;
  },

  async deleteDocument(id: string) {
    const res = await api.delete(`${BASE}/documents/${id}`);
    return res.data;
  },

  // Processing
  async processDocument(id: string) {
    const res = await api.post(`${BASE}/documents/${id}/process`);
    return res.data as ProcessingResponse;
  },

  async classifyDocument(id: string) {
    const res = await api.post(`${BASE}/documents/${id}/classify`);
    return res.data as ProcessingResponse;
  },

  async segmentDocument(id: string) {
    const res = await api.post(`${BASE}/documents/${id}/segment`);
    return res.data as ProcessingResponse;
  },

  async runGuardrails(id: string) {
    const res = await api.post(`${BASE}/documents/${id}/run-guardrails`);
    return res.data as ProcessingResponse;
  },

  async batchProcess(documentIds: string[]) {
    const res = await api.post(`${BASE}/documents/batch-process`, { document_ids: documentIds });
    return res.data;
  },

  // Validation
  async validateDocument(id: string, status: 'validated' | 'rejected', notes?: string) {
    const res = await api.patch(`${BASE}/documents/${id}/validate`, { status, notes });
    return res.data;
  },

  async validateParagraph(id: string, data: { status: 'validated' | 'rejected' | 'pending'; curator_notes?: string; taxonomy_tag?: string }) {
    const res = await api.patch(`${BASE}/paragraphs/${id}/validate`, data);
    return res.data;
  },

  async promoteDocument(id: string) {
    const res = await api.post(`${BASE}/documents/${id}/promote`);
    return res.data;
  },

  async getValidationQueue(page = 1, pageSize = 20) {
    const res = await api.get(`${BASE}/validation-queue`, { params: { page, page_size: pageSize } });
    return res.data as DocumentListResponse;
  },

  // Taxonomy
  async listTaxonomy() {
    const res = await api.get(`${BASE}/taxonomy`);
    return res.data as PandoraTaxonomy[];
  },

  async createTaxonomy(data: { domain: string; class_name: string; element_name: string; full_tag: string; description?: string }) {
    const res = await api.post(`${BASE}/taxonomy`, data);
    return res.data as PandoraTaxonomy;
  },

  async updateTaxonomy(id: string, data: { description?: string; is_active?: boolean }) {
    const res = await api.put(`${BASE}/taxonomy/${id}`, data);
    return res.data as PandoraTaxonomy;
  },

  async deleteTaxonomy(id: string) {
    const res = await api.delete(`${BASE}/taxonomy/${id}`);
    return res.data;
  },

  async seedTaxonomy() {
    const res = await api.post(`${BASE}/taxonomy/seed`);
    return res.data;
  },

  // Stats
  async getStats() {
    const res = await api.get(`${BASE}/stats`);
    return res.data as PipelineStats;
  },

  // Validated Content
  async getValidatedSummary() {
    const res = await api.get(`${BASE}/validated/summary`);
    return res.data as {
      glossary: number;
      jurisprudence: number;
      legislation: number;
      paragraphs: number;
      rules: number;
      total: number;
    };
  },

  async getValidatedGlossary(params: {
    search?: string;
    legal_domain?: string;
    page?: number;
    page_size?: number;
  } = {}) {
    const res = await api.get(`${BASE}/validated/glossary`, { params });
    return res.data as { items: any[]; total: number; page: number; page_size: number };
  },

  async getValidatedJurisprudence(params: {
    search?: string;
    court_name?: string;
    legal_domain?: string;
    page?: number;
    page_size?: number;
  } = {}) {
    const res = await api.get(`${BASE}/validated/jurisprudence`, { params });
    return res.data as { items: any[]; total: number; page: number; page_size: number };
  },

  async getValidatedLegislation(params: {
    search?: string;
    area?: string;
    page?: number;
    page_size?: number;
  } = {}) {
    const res = await api.get(`${BASE}/validated/legislation`, { params });
    return res.data as { items: any[]; total: number; page: number; page_size: number };
  },

  async getValidatedParagraphs(params: {
    tag_domain?: string;
    tag_class?: string;
    search?: string;
    page?: number;
    page_size?: number;
  } = {}) {
    const res = await api.get(`${BASE}/validated/paragraphs`, { params });
    return res.data as {
      items: any[];
      total: number;
      page: number;
      page_size: number;
      by_domain?: Record<string, number>;
    };
  },

  async getRules(params: {
    category?: string;
    page?: number;
    page_size?: number;
  } = {}) {
    const res = await api.get(`${BASE}/rules`, { params });
    return res.data as { items: any[]; total: number; page: number; page_size: number };
  },
};

export default pandoraAdminService;
